import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Select, Row, Col, Spin, Space, Empty } from "antd";
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { CalendarOutlined, BankOutlined, FilterOutlined, AppstoreOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

// Official Leave Colors aligned with Calendar & Attendance Standards
const LEAVE_DEFS = [
    { key: "total_cl",         label: "Casual Leave",    abbr: "CL",  color: "#CB00CC", bg: "#fdf4ff", border: "#f5d0fe" },
    { key: "total_sl",         label: "Sick Leave",      abbr: "SL",  color: "#FF0066", bg: "#fff1f2", border: "#fecaca" },
    { key: "total_el",         label: "Comp Off / EL",   abbr: "CO",  color: "#1FA2CC", bg: "#f0f9ff", border: "#bae6fd" },
    { key: "total_lop",        label: "NLB Days",        abbr: "NLB", color: "#bf4406", bg: "#fff1f2", border: "#fecaca" },
    { key: "total_permission", label: "Permission Logs",abbr: "Perm",color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
];

export default function OrganisationLeave() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState("ALL");

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        const q = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        axios.get(`${backUrl}/api/emp/departments/${q}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            setDepartments(res.data.results || res.data || []);
            setDepartmentId("ALL");
        }).catch(() => setDepartments([]));
    }, [organisationId, backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;
        if (departmentId !== "ALL") params.department_id = departmentId;

        axios.get(`${backUrl}/api/reports/organisation-leave-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(res.data || null))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, departmentId, month, year, backUrl, authData?.access]);

    const chartData = data
        ? LEAVE_DEFS
            .map(def => ({ name: def.abbr, fullName: def.label, value: Number(data[def.key] || 0), color: def.color }))
            .filter(i => i.value > 0)
        : [];

    const totalLeave = chartData.reduce((s, d) => s + d.value, 0);

    return (
        <Card
            variant="borderless"
            style={{
                borderRadius: 16, background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                border: "1px solid #e2e8f0", minHeight: 450,
                position: "relative",
            }}
            styles={{ body: { padding: "18px 20px" } }}
        >
            {/* Header & Filter Bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "linear-gradient(135deg, #0e497b 0%, #1e40af 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", boxShadow: "0 2px 8px rgba(14,73,123,0.25)", flexShrink: 0
                    }}>
                        <CalendarOutlined style={{ fontSize: 17 }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
                            Organisation Leave Summary
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                            {MONTHS[month - 1]} {year} &middot; Approved Leave Breakdown & Permissions
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Grid */}
            <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                <Col xs={12} sm={6}>
                    <Select
                        value={organisationId}
                        onChange={(v) => setOrganisationId(v || "ALL")}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onClear={() => setOrganisationId("ALL")}
                        placeholder="Organisation"
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        <Option value="ALL">
                            <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>All Orgs ({organisations.length})</span></Space>
                        </Option>
                        {organisations.map(o => (
                            <Option key={o.id} value={o.id}>
                                <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>{o.name}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={departmentId}
                        onChange={(v) => setDepartmentId(v || "ALL")}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onClear={() => setDepartmentId("ALL")}
                        placeholder="Department"
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        <Option value="ALL">
                            <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>All Depts ({departments.length})</span></Space>
                        </Option>
                        {departments.map(d => (
                            <Option key={d.id} value={d.id}>
                                <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>{d.name}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={month}
                        onChange={setMonth}
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        {MONTHS.map((m, i) => (
                            <Option key={i + 1} value={i + 1}>
                                <Space size={4}><CalendarOutlined style={{ color: "#2563eb" }} /> <span>{m}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col xs={12} sm={6}>
                    <Select
                        value={year}
                        onChange={setYear}
                        style={{ width: "100%" }}
                        size="middle"
                        popupMatchSelectWidth={false}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                    >
                        {YEARS.map(y => (
                            <Option key={y} value={y}>
                                <Space size={4}><FilterOutlined style={{ color: "#7c3aed" }} /> <span>{y}</span></Space>
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Approved Leave Breakdown...</div>
                </div>
            ) : !data || chartData.length === 0 ? (
                <Empty description="No approved leave records for selected filters" style={{ padding: "60px 0" }} />
            ) : (
                <>
                    {/* Summary Stat Cards */}
                    <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                        {LEAVE_DEFS.map(def => {
                            const val = data[def.key] || 0;
                            return (
                                <Col xs={12} sm={8} key={def.key} style={{ flex: "1 1 auto" }}>
                                    <div style={{
                                        background: def.bg, border: `1px solid ${def.border}`,
                                        borderRadius: 10, padding: "8px 10px", textAlign: "center",
                                    }}>
                                        <div style={{ fontSize: 10.5, fontWeight: 800, color: def.color, textTransform: "uppercase", marginBottom: 2 }}>
                                            {def.abbr}
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 900, color: def.color }}>{val}</div>
                                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{def.label}</div>
                                    </div>
                                </Col>
                            );
                        })}
                        <Col xs={12} sm={8} style={{ flex: "1 1 auto" }}>
                            <div style={{
                                background: "#f8fafc", border: "1px solid #e2e8f0",
                                borderRadius: 10, padding: "8px 10px", textAlign: "center",
                            }}>
                                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", marginBottom: 2 }}>
                                    TOTAL
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>{roundVal(totalLeave)}</div>
                                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>Approved Days</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Donut Chart */}
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={90}
                                paddingAngle={3}
                                label={({ name, value }) => `${name}: ${value}`}
                                labelLine={false}
                            >
                                {chartData.map((item, i) => (
                                    <Cell key={i} fill={item.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(v, name, props) => [
                                    `${v} days`,
                                    props.payload?.fullName || name,
                                ]}
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                            />
                            <Legend
                                formatter={(name, entry) => entry.payload?.fullName || name}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </>
            )}
        </Card>
    );
}

function roundVal(v) {
    return Math.round((v + Number.EPSILON) * 10) / 10;
}

